// client/src/pages/schedule/ScheduleList.js
import React, { useState, useEffect, useContext, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { AuthContext } from '../../context/AuthContext.js';
import { getScheduleList } from '../../api/scheduleApi.js';
import ScheduleRead from './ScheduleRead.js';
import ScheduleForm from './ScheduleForm.js';
import CommonDatePicker from "../../components/DatePicker/CommonDatePicker.js";
import { statusMap } from '../../utils/commonUtils';

function ScheduleList() {
  const { token, user } = useContext(AuthContext);
  const now = new Date();

  const [list, setList] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  // 검색 관련 상태
  const [searchType, setSearchType] = useState('subject');
  const [searchValue, setSearchValue] = useState('');
  const [startDate, setStartDate] = useState(now);
  const [endDate, setEndDate] = useState(now);

  const [page, setPage] = useState(1);
  const rowsPerPage = 10;
  const pageGroupSize = 10;

  // 선택된 일정 상태
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [isReadOpen, setIsReadOpen] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [showScheduleForm, setShowScheduleForm] = useState(false);

  const [statusFilter, setStatusFilter] = useState("request");
  const [isSearching, setIsSearching] = useState(false);
  const [searchParams, setSearchParams] = useState({});

  const toDateText = (d) => {
    if (!d) return '';
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    const ampm = hours < 12 ? '오전' : '오후';
    const hour12 = hours % 12 || 12;
    return `${year}-${month}-${day} ${ampm} ${hour12}:${minutes}:${seconds}`;
  };




  // 예약일시 포맷 변환
  const formatReservation = (schedule) => {
    const startDate = schedule.start_date;
    const endDate = schedule.end_date;
    const startTime = schedule.start_time ? schedule.start_time.substring(0, 5) : '';
    const endTime = schedule.end_time ? schedule.end_time.substring(0, 5) : '';

    // 날짜가 같으면 한 번만 표시
    if (startDate === endDate) {
      return `${startDate} ${startTime} ~ ${endTime}`;
    } else {
      return `${startDate} ${startTime} ~ ${endDate} ${endTime}`;
    }
  };

  // 새로고침
  const handleRefresh = useCallback(async () => {

    if (!token || !user?.userid) return;

    if (isSearching && Object.keys(searchParams).length === 0) {
      return;
    }

    try {
      const params = isSearching
        ? { ...searchParams, status: statusFilter, page, rowsPerPage, event_type: 'W' }
        : { status: statusFilter, page, rowsPerPage, event_type: 'W' };
      const { rows, count } = await getScheduleList(params, token);
      setList(rows || []);
      setTotalCount(count || 0);
    } catch (err) {
      console.error(`${statusFilter} 일정 불러오기 실패:`, err);
    }
  }, [token, user?.userid, statusFilter, page, searchParams, isSearching]);

  // 초기 로딩 + 검색 조건 변경 시 갱신
  useEffect(() => {
    handleRefresh();
  }, [handleRefresh, searchParams]);

  // 검색
  const handleSearch = () => {
    if (searchType === 'subject' && (!searchValue || searchValue.trim() === '')) {
      alert("검색어가 지정되지 않았습니다.");
      return;
    }
    if (searchType === 'date') {
      if (!startDate || !endDate) {
        alert("검색일이 지정되지 않았습니다.");
        return;
      }
      if (endDate < startDate) {
        alert("종료일은 시작일보다 늦어야 합니다.");
        return;
      }
    }

    const params = {};
    if (searchType === 'subject') params.subject = searchValue;
    if (searchType === 'writer') params.writer = searchValue;
    if (searchType === 'date') {
      params.startDate = toDateText(startDate);
      params.endDate = toDateText(endDate);
    }

    setPage(1);
    setSearchParams(params);
    setIsSearching(true);
  };

  const handleReset = () => {
    setIsSearching(false);
    setPage(1);
    setSearchType('subject');
    setSearchValue('');
    setStartDate(now);
    setEndDate(now);
    setSearchParams({});
  };

  const handleSearchTypeChange = (e) => {
    setSearchType(e.target.value);
    setSearchValue('');
    setStartDate(now);
    setEndDate(now);
  };

  const totalPages = Math.ceil(totalCount / rowsPerPage);
  const currentGroup = Math.floor((page - 1) / pageGroupSize);
  const startPage = currentGroup * pageGroupSize + 1;
  const endPage = Math.min(startPage + pageGroupSize - 1, totalPages);

  const handleRowClick = (schedule) => {
    setSelectedSchedule(schedule);
    setIsReadOpen(true);
  };

  const getTitle = () => {
    return statusMap[statusFilter] + " 공유 일정" || "일정";
  };

  // ✅ UI 렌더링
  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">

          {/* 상단 타이틀 및 버튼 */}
          <div className="d-flex justify-content-between align-items-center mb-3 p-3 rounded shadow"
            style={{ background: 'linear-gradient(90deg, #607d8b 0%, #cfd8dc 100%)', color: '#fff' }}>
            <h4 className="mb-0 fw-bold">
              {getTitle()}
              {isSearching && (
                <span className="ms-2 fw-bold" style={{ fontSize: '0.75em', opacity: 1 }}>
                  (검색 결과)
                </span>
              )}
            </h4>
            <div className="text-end">
              <div className="btn-group gap-1">
                <button className="btn btn-primary btn-sm" onClick={() => setShowScheduleForm(true)}>
                  일정 등록
                </button>
                <button
                  className="btn btn-light btn-sm d-flex align-items-center"
                  onClick={() => window.location.reload()}
                >
                  <i className="bi bi-arrow-clockwise me-1"></i> 새로고침
                </button>
              </div>
            </div>
          </div>

          {/* 검색 영역 */}
          <div className="card mb-3">
            <div className="card-body row g-2 align-items-center" style={{ paddingTop: "10px", paddingBottom: "10px" }} >
              <div className="col-auto">
                <select
                  className="form-select"
                  value={searchType}
                  onChange={handleSearchTypeChange}
                >
                  <option value="subject">제목</option>
                  <option value="writer">작성자</option>
                  <option value="date">일정일자</option>
                </select>
              </div>

              {(searchType === 'subject' || searchType === 'writer') && (
                <div className="col-md-4">
                  <input
                    type="text"
                    className="form-control"
                    placeholder={searchType === 'subject' ? "제목 입력" : "작성자 입력"}
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleSearch(); // ✅ 엔터 시 검색 실행
                      }
                    }}
                  />
                </div>
              )}

              {searchType === 'date' && (
                <div className="col-auto d-flex align-items-center gap-2">
                  <CommonDatePicker
                    value={startDate}
                    onChange={(date) => setStartDate(date)}
                    placeholder="시작일 선택"
                    showTime={false}
                  />
                  <span>~</span>
                  <CommonDatePicker
                    value={endDate}
                    onChange={(date) => setEndDate(date)}
                    placeholder="종료일 선택"
                    showTime={false}
                  />
                </div>
              )}

              <div className="col-auto">
                <button className="btn btn-primary" onClick={handleSearch}>검색</button>
              </div>
              <div className="col-auto">
                <button className="btn btn-outline-secondary" onClick={handleReset}>초기화</button>
              </div>

              <div className="col-auto ms-auto">
                <div className="btn-group" role="group">
                  <button
                    className={`btn btn-sm ${statusFilter === "request" ? "btn-warning" : "btn-outline-warning"}`}
                    onClick={() => setStatusFilter("request")}
                  >등록대기</button>
                  <button
                    className={`btn btn-sm ${statusFilter === "reject" ? "btn-danger" : "btn-outline-danger"}`}
                    onClick={() => setStatusFilter("reject")}
                  >반려</button>
                  <button
                    className={`btn btn-sm ${statusFilter === "draft" ? "btn-secondary" : "btn-outline-secondary"}`}
                    onClick={() => setStatusFilter("draft")}
                  >회수</button>
                </div>
              </div>
            </div>
          </div>

          {/* 리스트 테이블 */}
          <div className="table-responsive">
            <table className="table table-nowrap align-middle table-hover mb-0">
              <thead className="table-light">
                <tr>
                  <th style={{ width: '33%' }} className="text-left">제목</th>
                  <th style={{ width: '6%' }} className="text-center">작성자</th>
                  <th style={{ width: '13%' }} className="text-center">부서</th>
                  <th style={{ width: '13%' }} className="text-center">작성일자</th>
                  <th style={{ width: '28%' }} className="text-center">예약 시작/종료</th>
                  <th style={{ width: '7%' }} className="text-center">문서상태</th>
                </tr>
              </thead>
              <tbody>
                {list.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="text-center text-muted" style={{ height: '200px' }}>
                      데이터가 존재하지 않습니다.
                    </td>
                  </tr>
                ) : (
                  list.map(schedule => (
                    <tr key={schedule.id} onClick={() => handleRowClick(schedule)} style={{ cursor: 'pointer' }}>
                      <td>{schedule.title}</td>
                      <td className="text-center">{schedule.writer_name}</td>
                      <td className="text-center">{schedule.dept_name}</td>
                      <td className="text-center">{formatDateTime(schedule.created_at)}</td>
                      <td className="text-center">{formatReservation(schedule)}</td>
                      <td className="text-center">{statusMap[schedule.status]}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* 페이징 네비게이터 */}
          <nav className="mt-4 position-relative d-flex justify-content-center align-items-center">
            {/* 왼쪽: 총 건수 표시 */}
            <div
              className="fw-bold fs-6 text-dark position-absolute start-0"
              style={{ top: "50%", transform: "translateY(-50%)" }}
            >
              <span className="badge bg-secondary fw-bold fs-6">
                {totalCount > 0
                  ? `${page} / ${totalPages} (총 : ${totalCount})`
                  : `(총 : 0)`
                }
              </span>
            </div>

            {/* 가운데: 페이지 버튼 */}
            <div className="flex-grow-1 d-flex justify-content-center">
              <ul className="pagination mb-0">
                {/* 이전 버튼 */}
                <li className={`page-item ${page <= 1 ? 'disabled' : ''}`}>
                  <button
                    className="page-link"
                    onClick={() => page > 1 && setPage(page - 1)}
                  >
                    &laquo; 이전
                  </button>
                </li>

                {/* 페이지 번호 */}
                {Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i).map(p => (
                  <li key={p} className={`page-item ${p === page ? 'active' : ''}`}>
                    <button className="page-link" onClick={() => setPage(p)}>{p}</button>
                  </li>
                ))}

                {/* 다음 버튼 */}
                <li className={`page-item ${page >= totalPages ? 'disabled' : ''}`}>
                  <button
                    className="page-link"
                    onClick={() => page < totalPages && setPage(page + 1)}
                  >
                    다음 &raquo;
                  </button>
                </li>
              </ul>
            </div>
          </nav>

          {isReadOpen && selectedSchedule && createPortal(
            <div className="schedule_backdrop">
              <div className="schedule_read_modal">
                <ScheduleRead
                  data={selectedSchedule}
                  onClose={(needRefresh) => {
                    setIsReadOpen(false);
                    setSelectedSchedule(null);
                    if (needRefresh) {
                      handleRefresh(); // ✅ 닫을 때 리스트 갱신
                    }
                  }}
                  onEdit={() => {
                    setIsReadOpen(false);
                    setIsFormOpen(true);
                  }}
                />
              </div>
            </div>,
            document.body
          )}
          
          {/* ✅ 일정 수정 모달 */}
          {isFormOpen && selectedSchedule && createPortal(
            <div className="schedule_backdrop">
              <div className="schedule_form_modal">
                <ScheduleForm
                  actionType="edit"
                  isModal={true}
                  schedule={selectedSchedule}
                  onClose={() => setIsFormOpen(false)}
                  onRefresh={() => {
                    setIsFormOpen(false);
                    handleRefresh();
                  }}
                />
              </div>
            </div>,
            document.body
          )}

          {/* ✅ 일정 등록 모달 */}
          {showScheduleForm && createPortal(
            <div className="schedule_backdrop">
              <div className="schedule_form_modal">
                <ScheduleForm
                  actionType="create"
                  isModal={true}
                  onClose={() => setShowScheduleForm(false)}
                  onRefresh={() => {
                    setShowScheduleForm(false);
                    handleRefresh();
                  }}
                />
              </div>
            </div>,
            document.body
          )}

        </div>
      </div>
    </div>
  );
}

export default ScheduleList;